import React, { useRef, useState } from 'react';
import { Stack, Typography, Button, Input, Box, Alert } from '@mui/material';
import { ArticleOutlined } from '@mui/icons-material';
import { FeedbackCircle as FileUploadContainer } from '@/components/shared/FeedbackCircle';
import { useRouter, useFeatureFlag } from '@/hooks';
import {
  GenericDialog as ConfirmationDialog,
  GenericDialog as FileErrorDialog,
} from '@/components/shared/GenericDialog';
import { abyssColors } from '@/theme/colors';
import {
  ServiceInput,
  useUploadEsSheetMutation,
  useValidateEsSheetMutation,
} from '@/__generated__/graphql';
import { ValidateFileModal } from '../Analysis/Idms/Modals/ValidateFileModal';
import { useSetAtom } from 'jotai';
import { snackbarMessage } from '@/state';
import { esSheetColumns, serviceKeys } from './data';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { Link } from 'react-router-dom';
import * as ExcelJS from 'exceljs';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';

type Props = {
  deploymentName: string | undefined;
  structureId: string;
};

type FileError = { title: string; description: string };
const fileErrors = {
  typeError: {
    title: 'Incorrect File format',
    description: 'You have uploaded the incorrect file type. Please upload a .xlsx file.',
  },
  sizeError: {
    title: 'File size exceeded',
    description:
      'The file size exceeds the limit of 50MB. Please upload a file with a size less than 50MB.',
  },
  validationError: {
    title: 'Incorrect “Template headers”',
    description:
      'The template headers in the file you uploaded are incorrect. Please upload a file with the correct template headers.',
  },
  noData: {
    title: 'File has no data',
    description:
      'The file you uploaded does not have any data. Please upload a file with data to proceed.',
  },
};

export const UploadEsSheet = ({ deploymentName, structureId }: Props) => {
  const fileInputReference = useRef<HTMLInputElement | undefined>(undefined);
  const canAccessEsSheet = useFeatureFlag(['ait-task-lead', 'ait-engineer', 'admin']);
  const [fileError, setFileError] = useState<FileError | undefined>(undefined);
  const [isValidating, setIsValidating] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const setSnackbarMessage = useSetAtom(snackbarMessage);
  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const router = useRouter();

  const [esSheet, setEsSheet] = useState<{ headers: string[]; data: string[][] } | undefined>(
    undefined
  );

  const [uploadEsSheet, { loading }] = useUploadEsSheetMutation();

  const [validateEsSheet] = useValidateEsSheetMutation();

  const validateFile = (selectedFile: File) => {
    const reader = new FileReader();
    setIsValidating(true);

    reader.addEventListener('load', async () => {
      const arrayBuffer = reader.result as ArrayBuffer;

      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      const sheet = workbook.worksheets[0];

      const sheetData: string[][] = [];

      sheet.eachRow((row) => {
        const rowData: string[] = [];

        // For each cell in the row, check if it exists. If not, push an empty string.
        const maxCol = row.cellCount;
        for (let colIndex = 1; colIndex <= maxCol; colIndex++) {
          const cell = row.getCell(colIndex);

          rowData.push(cell.text.trim() || '');
        }

        sheetData.push(rowData);
      });

      if (sheetData.length === 0) {
        setFileError(fileErrors.noData);
        return;
      }

      const headers = sheetData[0].map((header) => header.trim());
      const data = sheetData.slice(1).map((row) => row.map((rowData) => rowData.trim()));

      setEsSheet({ headers, data });

      validateEsSheet({ variables: { headers } })
        .then(({ data: response }) => {
          if (response?.validateEsSheet) setOpenConfirmationDialog(true);
        })
        .catch(() => {
          setFileError(fileErrors.validationError);
        })
        .finally(() => {
          setIsValidating(false);
        });
    });

    reader.readAsArrayBuffer(selectedFile);
  };

  const handleUploadEsSheet = async () => {
    const { headers, data: sheetData } = esSheet || {};
    if (!headers || !sheetData) return;

    const result = sheetData.map((row) => {
      const templateFieldData = {} as Record<string, string>;
      const serviceObject = {} as Record<string, string>;

      headers.forEach((header, index) => {
        const mappedKey = esSheetColumns[header as keyof typeof esSheetColumns];
        if (serviceKeys.has(mappedKey)) {
          serviceObject[mappedKey] = row[index];
        } else {
          templateFieldData[mappedKey] = row[index];
        }
      });

      const service: ServiceInput = {
        group: serviceObject?.group ?? 'Unknown Services',
        ...(serviceObject?.code ? { code: serviceObject.code } : {}),
      };

      return {
        service,
        tagName: row[0],
        templateFieldData: JSON.stringify(templateFieldData),
      };
    });

    uploadEsSheet({
      variables: {
        input: {
          headers,
          structureId,
          data: result,
        },
      },
    })
      .then(() => {
        setOpenConfirmationDialog(false);
        setSnackbarMessage({
          shouldShow: true,
          content: <Alert severity="success">ES sheet uploaded successfully</Alert>,
        });
        router.push(`/analysis/${structureId}/risk`);
      })
      .catch(() => {
        setSnackbarMessage({
          shouldShow: true,
          content: <Alert severity="error">ES sheet upload failed</Alert>,
        });
      });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    setFileName(selectedFile.name);

    const isXlsx = selectedFile.name.endsWith('.xlsx');
    const fileSize = selectedFile.size / (1024 * 1024);
    const isError = !isXlsx || fileSize > 50;
    if (isError) {
      setFileError(isXlsx ? fileErrors.sizeError : fileErrors.typeError);
      return;
    }

    const fileInput = event.target;
    fileInput.value = '';

    validateFile(selectedFile);
  };

  const handleTypeErrorDialog = () => {
    setFileError(undefined);
    if (fileInputReference.current) {
      fileInputReference.current.value = '';
    }
  };

  const handleFileValidate = () => {
    if (fileInputReference.current) {
      fileInputReference.current.click();
    }
  };

  const handleCancelUpload = () => {
    setOpenConfirmationDialog(false);
  };

  return (
    <Box>
      <Stack data-testid="top-title">
        <Typography color="text.primary" component="h1" fontSize="3.2rem" fontWeight={700}>
          {deploymentName}
        </Typography>
        <Typography variant="body1" fontSize="1.6rem" sx={{ color: abyssColors.primary[400] }}>
          This is Marine terminal platform without any deployment
        </Typography>
      </Stack>
      <Stack alignItems="center" data-testid="file-upload-container">
        <FileUploadContainer
          text={
            canAccessEsSheet ? (
              <Box sx={{ fontSize: '1.6rem' }}>
                <span>Upload the ES sheet here, or download </span>
                <Link to={'/assets/es-sheet-template/es-sheet-template.xlsx'}>template.xlsx</Link>
              </Box>
            ) : (
              <span style={{ fontSize: '1.6rem' }}>
                No data for {deploymentName ?? 'the platform'} has been uploaded yet by the Abyss
                Fabric team.{' '}
                <a href="mailto:fabric-support@abysssolutions.com.au">Contact Support?</a>
              </span>
            )
          }
          icon={<ArticleOutlined sx={{ color: 'text.secondary', fontSize: '7rem' }} />}
          backgroundColor="#F8FAFD"
          diameter={550}
          actionButton={
            canAccessEsSheet ? (
              <Button
                variant="contained"
                sx={{ containedButtonStyle }}
                size="large"
                startIcon={<AbyssIcon iconName="file-upload-line" />}
                onClick={handleFileValidate}
              >
                Upload File
              </Button>
            ) : (
              <></>
            )
          }
        />
        <Input
          inputRef={fileInputReference}
          hidden
          type="file"
          id="upload-file"
          style={{ display: 'none' }}
          inputProps={{ accept: '.xlsx' }}
          onChange={handleFileSelect}
        />
      </Stack>
      {fileError && (
        <FileErrorDialog
          open={!!fileError}
          onClose={() => {}}
          title={fileError.title}
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              {fileError.description}
            </Typography>
          }
          actions={
            <Button
              variant="contained"
              onClick={() => handleTypeErrorDialog()}
              sx={containedButtonStyle}
            >
              Got It
            </Button>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}

      <ValidateFileModal open={isValidating} />

      <ConfirmationDialog
        open={openConfirmationDialog}
        onClose={() => {}}
        title="Success"
        content={
          <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
            {`File validation complete. Please confirm to upload ${fileName ?? 'ES.xlsx'} to Abyss Fabric.`}
          </Typography>
        }
        actions={
          <>
            <Button variant="text" onClick={() => handleCancelUpload()} sx={textButtonStyle}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => handleUploadEsSheet()}
              sx={containedButtonStyle}
              disabled={loading}
              startIcon={
                <AbyssIcon
                  iconName="file-upload-line"
                  style={{ width: '1.6rem', height: '1.6rem' }}
                />
              }
            >
              {loading ? 'Uploading' : 'Confirm'}
            </Button>
          </>
        }
        actionsStyles={{ mr: 3, pb: 3 }}
      />
    </Box>
  );
};
